/** Stable identity for Approvals / Request queue rows. */

export function normalizeDeskRequestId(id) {
  const value = String(id ?? "").trim();
  return value || "";
}

export function deskRequestIdsEqual(a, b) {
  const left = normalizeDeskRequestId(a);
  const right = normalizeDeskRequestId(b);
  return Boolean(left) && left === right;
}

export function findDeskRequest(rows, requestId) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const id = normalizeDeskRequestId(requestId);
  if (!id) return null;
  return rows.find((row) => deskRequestIdsEqual(row?.requestId, id)) || null;
}

function checklistTaskKey(row) {
  return String(
    row?.formData?.taskName ||
      row?.checklistName ||
      row?.checklistTask?.name ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Content key: true clones collapse; distinct tasks with colliding requestIds do not. */
export function deskItemDedupeKey(row) {
  if (!row) return "";
  const mode = String(row.deskMode || "");
  const parentId = String(
    row.onboardingRequestId ??
      row.confirmationRequestId ??
      row.transferRequestId ??
      row.separationRequestId ??
      row.leaveRequestId ??
      ""
  );
  const person = String(
    row.employeeCode || row.linkedEmployeeId || row.userName || row.employeeId || ""
  )
    .trim()
    .toLowerCase();
  if (mode === "checklist") {
    const status = String(row.status || "").trim().toLowerCase() || "pending";
    return `chk:${person}:${checklistTaskKey(row)}:${status}:${String(row.actionLabel || "")}`;
  }
  return [
    mode,
    normalizeDeskRequestId(row.requestId),
    row.notifyOnlyFyi ? "1" : "0",
    parentId,
    person,
  ].join(":");
}

/**
 * Drop true duplicate cards and guarantee unique requestId values so list
 * selection / React keys / detail lookup cannot collide.
 */
export function ensureUniqueDeskRequests(rows) {
  if (!Array.isArray(rows) || !rows.length) return rows || [];
  const seenContent = new Set();
  const usedIds = new Set();
  const out = [];

  for (const row of rows) {
    if (!row) continue;
    const contentKey = deskItemDedupeKey(row);
    if (contentKey && seenContent.has(contentKey)) continue;
    if (contentKey) seenContent.add(contentKey);

    let id = normalizeDeskRequestId(row.requestId);
    if (!id || usedIds.has(id)) {
      const base =
        id ||
        `desk_${String(contentKey || "row")
          .replace(/[^a-zA-Z0-9]+/g, "_")
          .replace(/^_|_$/g, "")
          .slice(0, 48) || "row"}`;
      let next = `${base}__${out.length}`;
      while (usedIds.has(next)) next = `${base}__${out.length}_${usedIds.size}`;
      id = next;
      out.push({ ...row, requestId: id });
    } else {
      out.push(row);
    }
    usedIds.add(id);
  }

  return out;
}
